/* Admin: Pages list + Page (sections) editor — the owner-editable CMS surface. */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import {
  Button, Field, Input, Textarea, Select, Card, Badge, EmptyState, Skeleton,
  ConfirmDialog, Icon, MediaUploaderField, RichTextEditor, showToast,
} from "../../ui/index.js";
import { usePages, usePage } from "../../data/hooks.js";
import { savePage } from "../../data/mutations.js";
import { formatRelative } from "../../utils/format.js";

export function AdminPagesListPage() {
  const { pages, loading } = usePages();
  return (
    <AdminShell>
      <div className="admin-main__header">
        <div>
          <h1 className="t-3xl">Pages</h1>
          <p>These are the pages of the public site. Edit the content of each.</p>
        </div>
      </div>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}</div>
      ) : pages.length === 0 ? (
        <Card><EmptyState title="No pages yet" body="Run the seed script to create the initial pages." /></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pages.map((p) => (
            <Card key={p.slug}>
              <div className="row row--between" style={{ alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 12, alignItems: "baseline" }}>
                    <span className="t-lg" style={{ fontWeight: 600 }}>{p.name || p.slug}</span>
                    <a href={p.url || `/${p.slug}`} target="_blank" rel="noreferrer" className="t-sm muted">
                      {p.url || `/${p.slug}`} <Icon.ExternalLink size={12} style={{ verticalAlign: "-2px" }} />
                    </a>
                  </div>
                  <div className="t-xs muted" style={{ marginTop: 4 }}>
                    Last edited {formatRelative(p.updatedAt)}{p.updatedBy ? ` by ${p.updatedBy}` : ""}
                  </div>
                </div>
                <Link to={`/admin/pages/${p.slug}/edit`} className="btn btn--primary">Edit</Link>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

const sectionDescription = (type) => ({
  hero: "Headline, subhead, CTA, image",
  body: "Rich text block",
  image: "Standalone image with caption",
  pillars: "Numbered pillar grid",
  cta: "Coloured call-to-action band",
}[type]);

export function AdminPageEditorPage() {
  const { slug } = useParams();
  const { page, sections: remoteSections, loading } = usePage(slug);

  const [sections, setSections] = useState([]);
  const [pageMeta, setPageMeta] = useState({ title: "", description: "", ogImage: null });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deletingSection, setDeletingSection] = useState(null);
  const [expandedMeta, setExpandedMeta] = useState(true);
  const seeded = useRef(false);

  // Seed local editing state once the page data first loads.
  useEffect(() => {
    if (!loading && page && !seeded.current) {
      setSections(remoteSections);
      setPageMeta({ title: page.title || page.name || "", description: page.description || "", ogImage: page.ogImage || null });
      seeded.current = true;
    }
  }, [loading, page, remoteSections]);

  if (loading && !seeded.current) {
    return <AdminShell><Skeleton h={400} /></AdminShell>;
  }
  if (!page) {
    return <AdminShell><EmptyState title="Page not found" body="That page slug doesn't exist." /></AdminShell>;
  }

  const updateSection = (id, key, value) => {
    setSections((ss) => ss.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
    setDirty(true);
  };
  const moveSection = (id, dir) => {
    setSections((ss) => {
      const idx = ss.findIndex((s) => s.id === id);
      const target = idx + dir;
      if (target < 0 || target >= ss.length) return ss;
      const copy = [...ss];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
    setDirty(true);
  };
  const deleteSection = (id) => {
    setSections((ss) => ss.filter((s) => s.id !== id));
    setDirty(true);
    setDeletingSection(null);
    showToast({ intent: "success", title: "Section removed (save to apply)" });
  };
  const addSection = (type) => {
    const id = "s-" + Date.now();
    const map = {
      hero: { id, type: "hero", eyebrow: "", headline: "New hero", subheadline: "", ctaLabel: "", ctaHref: "", image: null },
      body: { id, type: "body", heading: "New section", bodyHtml: "<p>Start writing...</p>" },
      image: { id, type: "image", image: null, caption: "", aspect: "16:9" },
      pillars: { id, type: "pillars", heading: "Pillars heading", items: [{ title: "Pillar 1", body: "" }, { title: "Pillar 2", body: "" }] },
      cta: { id, type: "cta", heading: "Heading", body: "", ctaLabel: "Click here", ctaHref: "/donate" },
    };
    setSections((ss) => [...ss, map[type]]);
    setDirty(true);
    setShowAdd(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await savePage(slug, {
        meta: { name: page.name, url: page.url, title: pageMeta.title, description: pageMeta.description, ogImage: pageMeta.ogImage },
        sections,
      });
      setDirty(false);
      showToast({ intent: "success", title: "Page saved" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell>
      <Link to="/admin/pages" className="back-link"><Icon.ArrowLeft size={14} />Back to pages</Link>

      <div className="savebar" style={{ marginTop: 8 }}>
        <h1 className="t-lg" style={{ margin: 0, flex: 1 }}>Editing: {page.name || slug}</h1>
        {dirty && <span className="badge badge--draft">Unsaved changes</span>}
        <a href={page.url || `/${slug}`} target="_blank" rel="noreferrer" className="btn btn--ghost">
          View public page <Icon.ExternalLink size={14} />
        </a>
        <Button variant="primary" loading={saving} onClick={save} disabled={!dirty}>{saving ? "Saving..." : "Save"}</Button>
      </div>

      <Card style={{ marginTop: 24 }}>
        <button onClick={() => setExpandedMeta((e) => !e)} style={{ width: "100%", background: "transparent", border: 0, padding: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 className="t-2xl" style={{ margin: 0 }}>Page settings</h2>
          <Icon.ChevronDown size={20} style={{ transform: expandedMeta ? "rotate(180deg)" : "none", transition: "transform 200ms", color: "var(--text-muted)" }} />
        </button>
        {expandedMeta && (
          <div className="stack-lg" style={{ marginTop: 24 }}>
            <Field label="Page title" helper="Shown in the browser tab and search results.">
              <Input value={pageMeta.title} onChange={(e) => { setPageMeta((m) => ({ ...m, title: e.target.value })); setDirty(true); }} />
            </Field>
            <Field label="Meta description" helper="Shown in Google results and link previews. 50–300 characters.">
              <Textarea value={pageMeta.description} onChange={(e) => { setPageMeta((m) => ({ ...m, description: e.target.value })); setDirty(true); }} rows={3} />
            </Field>
            <Field label="Social preview image">
              <MediaUploaderField value={pageMeta.ogImage} onChange={(v) => { setPageMeta((m) => ({ ...m, ogImage: v })); setDirty(true); }} path={`pages/${slug}/og`} aspect="1200/630" />
            </Field>
          </div>
        )}
      </Card>

      <h2 className="t-2xl" style={{ marginTop: 40, marginBottom: 16 }}>Sections</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {sections.map((s, idx) => (
          <SectionEditor
            key={s.id}
            slug={slug}
            section={s}
            isFirst={idx === 0}
            isLast={idx === sections.length - 1}
            onUpdate={(k, v) => updateSection(s.id, k, v)}
            onMoveUp={() => moveSection(s.id, -1)}
            onMoveDown={() => moveSection(s.id, 1)}
            onDelete={() => setDeletingSection(s)}
          />
        ))}
      </div>

      <div style={{ position: "relative" }}>
        <button className="add-section-btn" onClick={() => setShowAdd((s) => !s)}><Icon.Plus size={16} />Add a section</button>
        {showAdd && (
          <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 8, background: "var(--surface-elevated)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)", border: "1px solid var(--border)", padding: 6, zIndex: 10, display: "flex", flexDirection: "column", minWidth: 220 }}>
            {["hero", "body", "image", "pillars", "cta"].map((t) => (
              <button key={t} onClick={() => addSection(t)} style={{ height: 40, padding: "0 12px", border: 0, background: "transparent", textAlign: "left", borderRadius: 6, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ textTransform: "capitalize", fontWeight: 500 }}>{t}</span>
                <span className="t-xs muted">— {sectionDescription(t)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deletingSection}
        title="Delete this section?"
        body="The content will be lost when you save. This cannot be undone."
        confirmLabel="Delete section"
        destructive
        onConfirm={() => deleteSection(deletingSection.id)}
        onClose={() => setDeletingSection(null)}
      />
    </AdminShell>
  );
}

function SectionEditor({ slug, section, isFirst, isLast, onUpdate, onMoveUp, onMoveDown, onDelete }) {
  const storagePath = `pages/${slug}/${section.id}`;
  return (
    <div className="section-card">
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <button className="btn btn--ghost btn--sm btn--icon" disabled={isFirst} onClick={onMoveUp} aria-label="Move up" style={{ height: 24, width: 24 }}><Icon.ChevronUp size={14} /></button>
        <Icon.GripVertical size={16} style={{ color: "var(--text-muted)", margin: "0 auto" }} />
        <button className="btn btn--ghost btn--sm btn--icon" disabled={isLast} onClick={onMoveDown} aria-label="Move down" style={{ height: 24, width: 24 }}><Icon.ChevronDown size={14} /></button>
      </div>

      <div className="section-card__body">
        <div className="row row--between" style={{ marginBottom: 16 }}>
          <span className="section-card__type-label" style={{ margin: 0, textTransform: "capitalize" }}>{section.type}</span>
          <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Delete section"><Icon.Trash size={14} /></Button>
        </div>

        {section.type === "hero" && (
          <div className="stack-lg">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Eyebrow (small uppercase text)"><Input value={section.eyebrow || ""} onChange={(e) => onUpdate("eyebrow", e.target.value)} /></Field>
              <Field label="Headline"><Input value={section.headline || ""} onChange={(e) => onUpdate("headline", e.target.value)} /></Field>
            </div>
            <Field label="Subheadline"><Textarea value={section.subheadline || ""} onChange={(e) => onUpdate("subheadline", e.target.value)} rows={2} /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="CTA button label"><Input value={section.ctaLabel || ""} onChange={(e) => onUpdate("ctaLabel", e.target.value)} /></Field>
              <Field label="CTA link"><Input value={section.ctaHref || ""} onChange={(e) => onUpdate("ctaHref", e.target.value)} placeholder="/donate" /></Field>
            </div>
            <Field label="Hero image"><MediaUploaderField value={section.image} onChange={(v) => onUpdate("image", v)} path={storagePath} aspect="4/3" /></Field>
          </div>
        )}

        {section.type === "body" && (
          <div className="stack-lg">
            <Field label="Section heading (optional)"><Input value={section.heading || ""} onChange={(e) => onUpdate("heading", e.target.value)} /></Field>
            <Field label="Body content"><RichTextEditor value={section.bodyHtml || ""} onChange={(v) => onUpdate("bodyHtml", v)} instanceKey={section.id} /></Field>
          </div>
        )}

        {section.type === "image" && (
          <div className="stack-lg">
            <Field label="Image"><MediaUploaderField value={section.image} onChange={(v) => onUpdate("image", v)} path={storagePath} aspect="16/9" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
              <Field label="Aspect ratio">
                <Select value={section.aspect || "16:9"} onChange={(e) => onUpdate("aspect", e.target.value)}>
                  <option value="16:9">16:9 (widescreen)</option>
                  <option value="4:3">4:3 (standard)</option>
                  <option value="1:1">1:1 (square)</option>
                </Select>
              </Field>
            </div>
            <Field label="Caption (optional)"><Input value={section.caption || ""} onChange={(e) => onUpdate("caption", e.target.value)} /></Field>
          </div>
        )}

        {section.type === "pillars" && (
          <div className="stack-lg">
            <Field label="Pillars heading"><Input value={section.heading || ""} onChange={(e) => onUpdate("heading", e.target.value)} /></Field>
            <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {(section.items || []).map((it, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 12, alignItems: "flex-start" }}>
                  <Input value={it.title} onChange={(e) => onUpdate("items", section.items.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Pillar title" />
                  <Input value={it.body} onChange={(e) => onUpdate("items", section.items.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} placeholder="Pillar body" />
                  <Button variant="ghost" size="sm" onClick={() => onUpdate("items", section.items.filter((_, j) => j !== i))} aria-label="Remove pillar"><Icon.X size={14} /></Button>
                </div>
              ))}
            </div>
            <Button variant="secondary" size="sm" onClick={() => onUpdate("items", [...(section.items || []), { title: "", body: "" }])}><Icon.Plus size={14} />Add pillar</Button>
          </div>
        )}

        {section.type === "cta" && (
          <div className="stack-lg">
            <Field label="CTA heading"><Input value={section.heading || ""} onChange={(e) => onUpdate("heading", e.target.value)} /></Field>
            <Field label="CTA body (optional)"><Textarea value={section.body || ""} onChange={(e) => onUpdate("body", e.target.value)} rows={2} /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Button label"><Input value={section.ctaLabel || ""} onChange={(e) => onUpdate("ctaLabel", e.target.value)} /></Field>
              <Field label="Button link"><Input value={section.ctaHref || ""} onChange={(e) => onUpdate("ctaHref", e.target.value)} placeholder="/donate" /></Field>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPagesListPage;
